// User scene persistence: a "scene" captures the full config (base preset + dial tweaks +
// master volume) AND the per-diatom mixer state (gain/source/mute/solo). Stored in
// localStorage; importable/exportable as JSON. See studio.ts / App.ts for capture+apply.
import type { Excitation } from './studio';

const KEY = 'diatom.scenes.v1';

export interface SceneDiatom {
  gain: number;
  excitation: Excitation;
  muted: boolean;
  soloed: boolean;
}

export interface Scene {
  v: 1;
  name: string;
  presetId: string;
  dials: { calmActive: number; darkBright: number; pureRough: number; roaming: number };
  masterVolume: number;
  diatoms: Record<string, SceneDiatom>;
  /** active granular sound bank id (or 'none'); absent on older scenes = none. */
  excitationBank?: string;
  /** grain-engine tuning; absent on older scenes = leave current. */
  grainTuning?: { targetRms: number; grainMs: number; cloudGain: number; cloudMs: number };
  /** ambient sound-bank layer level (0..1); absent on older scenes = 0. */
  ambientLevel?: number;
  /** master drive (overdrive amount); absent = the preset's default. */
  drive?: number;
}

export function listScenes(): Scene[] {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((s) => s && typeof s.name === 'string') : [];
  } catch {
    return [];
  }
}

export function saveScene(scene: Scene): void {
  const all = listScenes().filter((s) => s.name !== scene.name);
  all.push(scene);
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch (e) {
    console.warn('[diatom] could not save scene', e);
  }
}

export function removeScene(name: string): void {
  localStorage.setItem(KEY, JSON.stringify(listScenes().filter((s) => s.name !== name)));
}

export function getScene(name: string): Scene | undefined {
  return listScenes().find((s) => s.name === name);
}

/** Validate + coerce parsed JSON into a Scene (or null). */
export function parseScene(text: string): Scene | null {
  try {
    const o = JSON.parse(text);
    if (o && typeof o.name === 'string' && typeof o.presetId === 'string' && o.dials) {
      return { v: 1, diatoms: {}, masterVolume: 1, ...o };
    }
  } catch {
    /* ignore */
  }
  return null;
}
